// The signed-in staff member, the brand and the program's features as hooks — the shapes the legacy pages read
// (SessionView, Brand, isEnabled('CheckBalance')), filled from the open loop API.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { API_URL, api, BffError, hasAccessToken, pascal, providerName, refresh, setAccessToken } from './bff';

export interface SessionUser {
  /** The staff member's id (the console compares it with who asked for a load). */
  id?: string;
  email: string;
  displayName: string;
  category: 'Administrator' | 'Cardholder';
  role: string;
  programId: string | null;
  programDisplayName: string;
  provider: string;
  internal: boolean;
  cardholderId: string | null;
  /** The cardholder's card, by its id in the API (what the card pages read); null before a card is issued. */
  providerUserReference: string | null;
  cardStatus: string | null;
}

export interface SessionView {
  authenticated: boolean;
  user: SessionUser | null;
  activeProvider: string | null;
  csrfToken: string;
  twoFactorRequired?: boolean;
}

export const SESSION_KEY = ['bff', 'session'] as const;

const SIGNED_OUT: SessionView = { authenticated: false, user: null, activeProvider: null, csrfToken: '' };

interface Me {
  type: 'staff';
  id: string;
  email: string;
  displayName: string;
  role: string;
  program: { id: string; displayName: string; isInternal: boolean; provider: string | null };
}

/**
 * The processor QOR's own staff look at (the topbar's picker): kept in this browser only, per person — a view
 * filter, not a permission (the API decides what each person reaches). Nothing chosen until they choose.
 */
const PROVIDER_KEY = 'ol_admin_active_provider';
export function storedProvider(email: string): string | null {
  try {
    const all = JSON.parse(localStorage.getItem(PROVIDER_KEY) ?? '{}') as Record<string, string>;
    return all[email] ?? null;
  } catch {
    return null;
  }
}
export function storeProvider(email: string, provider: string) {
  try {
    const all = JSON.parse(localStorage.getItem(PROVIDER_KEY) ?? '{}') as Record<string, string>;
    localStorage.setItem(PROVIDER_KEY, JSON.stringify({ ...all, [email]: provider }));
  } catch {
    // private window, storage blocked: the choice lasts until the page is reloaded
  }
}

/** Who is signed in: the access token in memory, else the refresh cookie; signed out when neither works. */
export async function fetchSession(): Promise<SessionView> {
  if (!hasAccessToken() && !(await refresh())) return SIGNED_OUT;
  try {
    const me = await api<Me>('GET', '/auth/me');
    return {
      authenticated: true,
      activeProvider: me.program.isInternal ? storedProvider(me.email) : null,
      csrfToken: '',
      user: {
        id: me.id,
        email: me.email,
        displayName: me.displayName,
        category: 'Administrator',
        role: pascal(me.role) ?? me.role,
        programId: me.program.id,
        programDisplayName: me.program.displayName,
        provider: providerName(me.program.provider),
        internal: me.program.isInternal,
        cardholderId: null,
        providerUserReference: null,
        cardStatus: null,
      },
    };
  } catch (err) {
    if (err instanceof BffError && err.status === 401) {
      setAccessToken(null);
      return SIGNED_OUT;
    }
    throw err;
  }
}

export function useSession() {
  return useQuery({ queryKey: SESSION_KEY, queryFn: fetchSession, staleTime: 60_000 });
}

/** After sign-in / a new card / sign-out: the new session everywhere. */
export function useSetSession() {
  const client = useQueryClient();
  return (view: SessionView) => {
    client.setQueryData(SESSION_KEY, view);
  };
}

/** Read the session again (a card was just issued or replaced) and put it everywhere. */
export const refreshSession = fetchSession;

export interface Brand {
  displayName: string;
  logoUrl: string | null;
  logoUrlDark: string | null;
  primaryColor: string;
  secondaryColor: string;
}

export const DEFAULT_BRAND: Brand = {
  displayName: 'QOR',
  logoUrl: '/images/qor-logo-mark.png',
  logoUrlDark: '/images/qor-logo-sidebar.png',
  primaryColor: '#12c8c0',
  secondaryColor: '#040a19',
};

const API_ORIGIN = API_URL.replace(/\/api\/v1$/, '');

/** The brand of the domain this page is on (never fails: QOR's on any error), applied as the CSS variables. */
export function useBrand(signedInAs: string | null | undefined): Brand {
  const q = useQuery({
    queryKey: ['bff', 'brand', window.location.hostname, signedInAs ?? null],
    queryFn: async (): Promise<Brand> => {
      const b = await api<{
        displayName: string;
        logoUrl: string | null;
        primaryColor: string;
        secondaryColor: string;
      }>('GET', `/public/brand?host=${encodeURIComponent(window.location.hostname)}`);
      const logo = b.logoUrl ? `${API_ORIGIN}${b.logoUrl}` : null;
      return {
        displayName: b.displayName,
        logoUrl: logo,
        logoUrlDark: logo,
        primaryColor: b.primaryColor,
        secondaryColor: b.secondaryColor,
      };
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  const brand = q.data ?? DEFAULT_BRAND;
  useEffect(() => {
    const root = document.documentElement.style;
    root.setProperty('--qor-accent', brand.primaryColor);
    root.setProperty('--qor-on-accent', luminance(brand.primaryColor) > 0.18 ? '#04121f' : '#ffffff');
    // the theme is dark: a light secondary colour would put light text on a light page, so QOR's is kept
    const dark = luminance(brand.secondaryColor) < 0.12;
    root.setProperty('--qor-black', dark ? brand.secondaryColor : DEFAULT_BRAND.secondaryColor);
  }, [brand.primaryColor, brand.secondaryColor]);
  return brand;
}

/** WCAG relative luminance of "#rgb" / "#rrggbb" (0 black .. 1 white); 0.5 for anything else. */
export function luminance(color: string): number {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim())?.[1];
  if (!hex) return 0.5;
  const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const v = parseInt(full.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** "check_balance" -> "CheckBalance": the legacy pages name features in PascalCase. */
const featureName = (key: string) => pascal(key) ?? key;

/** Feature flags of the signed-in staff member's own program (a client's staff). Fail open: unknown = enabled. */
export function useFeatures(signedInAs: string | null | undefined) {
  const session = useSession();
  const programId = session.data?.user?.programId ?? null;
  const q = useQuery({
    queryKey: ['bff', 'features', signedInAs ?? null, programId],
    queryFn: async () => {
      const flags = await api<Record<string, boolean>>('GET', `/admin/programs/${programId}/features`);
      return Object.entries(flags)
        .filter(([, on]) => !on)
        .map(([key]) => featureName(key));
    },
    staleTime: 60_000,
    retry: false,
    enabled: !!signedInAs && !!programId,
  });
  const disabled = new Set((q.data ?? []).map((f) => f.toLowerCase()));
  return { isEnabled: (feature: string) => !disabled.has(feature.toLowerCase()), loaded: q.isFetched || !signedInAs };
}

/** "<page> - <brand>" as the legacy <title>. */
export function useTitle(title: string, brand: Pick<Brand, 'displayName'>) {
  useEffect(() => {
    document.title = `${title} - ${brand.displayName}`;
  }, [title, brand.displayName]);
}
